import { DATE_FORMATS, PostStatus } from "@/constants";
import { useDeletePosts, useGetPosts, usePublishPost } from "@/hooks/query-hooks/usePosts";
import { Divider, Select, Skeleton, Space, Tag } from "antd";
import { ColumnsType } from "antd/es/table";
import { TableRowSelection } from "antd/es/table/interface";
import dayjs from "dayjs";
import { useState } from "react";
import { Helmet } from 'react-helmet-async';
import { AnimatedComponent, AntdTable, DeleteConfirmationModal, PageTitle } from "../common";
import { CreateNewPost } from "./CreateNewPost";
import { EditPostModal } from "./EditPostModal";
import { PostDetailModal } from "./PostDetailModal";

export function Posts() {

    const [currentPage, setCurrentPage] = useState(1);

    const { data, isLoading } = useGetPosts(currentPage);

    const { content: posts, totalElements, size } = data || {};

    const { mutate, isDeleting } = useDeletePosts();
    const { mutate: publishPost, isPublishing } = usePublishPost();

    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

    const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
        setSelectedRowKeys(newSelectedRowKeys);
    };

    const rowSelection: TableRowSelection<Post> = {
        selectedRowKeys,
        onChange: onSelectChange,
        type: "checkbox"
    }

    const onDeleteMultiplePosts = () => {
        if (selectedRowKeys.length === 0) return;

        mutate({
            ids: selectedRowKeys as number[]
        }, {
            onSuccess: () => {
                setSelectedRowKeys([]);
            }
        });
    }

    const columns: ColumnsType<Post> = [
        {
            title: "Post",
            dataIndex: "title",
            key: "title",
            render: (value, record) => (
                <div
                    className="grid grid-cols-6 gap-4"
                >
                    <div
                        className="col-span-2"
                    >
                        {
                            record.coverImage ? (
                                <img
                                    src={record.coverImage}
                                    alt={"Cover Image"}
                                    className="!h-20 !aspect-video object-cover rounded-md border border-neutral-300 bg-gray-100 "
                                />
                            ) : (
                                <Skeleton.Image />
                            )
                        }
                    </div>
                    <div
                        className="group col-span-4 flex items-center"
                    >
                        <article
                            className="space-y-1 group-hover:hidden pointer-events-none"
                        >
                            <h2
                                className="text-base font-semibold line-clamp-2"
                            >
                                {value}
                            </h2>
                            <p
                                className=" line-clamp-1"
                            >
                                {record.body}
                            </p>
                        </article>

                        <div
                            className="group-hover:block hidden self-center "
                        >
                            <PostDetailModal
                                {...record}
                            />
                        </div>
                    </div>
                </div>
            ),
            onCell: () => ({
                style: {
                    maxWidth: 200
                }
            }),
            onHeaderCell: () => ({
                style: {
                    textAlign: "center",
                }
            })
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (status, record) => status === PostStatus.Published ? (
                <Tag
                    color={status === PostStatus.Published ? "green" : "blue"}
                    className="!h-8 !flex !items-center !justify-center w-fit !mx-auto"
                >
                    {status}
                </Tag>
            ) : (
                <Select
                    options={Object.values(PostStatus).map((status) => ({
                        label: status,
                        value: status
                    }))}
                    value={PostStatus.Draft}
                    className="[&>.ant-select-selector]:!rounded-md [&>.ant-select-selector]:!px-2 [&>.ant-select-selector]:!bg-blue-100/50 [&>.ant-select-selector>.ant-select-selection-wrap>.ant-select-selection-item]:!text-blue-500 [&>.ant-select-selector>.ant-select-selection-wrap>.ant-select-selection-item]:!text-xs "
                    dropdownStyle={{
                        minWidth: 120
                    }}
                    onChange={(value) => {
                        if (value === PostStatus.Published) {
                            publishPost(record.id)
                        }
                    }}
                    loading={isPublishing}
                />
            ),
            align: "center",
            filters: Object.values(PostStatus).map((status) => ({
                text: status,
                value: status
            })),
            onFilter: (value, record) => record.status === value,
        },
        {
            title: "Created At",
            dataIndex: "createdAt",
            key: "createdAt",
            render: (value) => dayjs(value).format(DATE_FORMATS.DAY_MONTH_FORMAT),
            align: "center"
        },
        {
            title: "Actions",
            dataIndex: "actions",
            render: (_, record) => (
                <Space>
                    <EditPostModal
                        {...record}
                    />
                    <DeleteConfirmationModal
                        title="Delete Post"
                        description="Are you sure you want to delete this post?"
                        onDelete={() => mutate({ ids: [record.id] })}
                        isDeleting={isDeleting}
                    />
                </Space>
            ),
            align: "center"
        }
    ]

    return (
        <AnimatedComponent>
            <Helmet>
                <title>သတင်းနှင့် ဓာတ်ပုံများ</title>
                <meta name="description" content="ဓမ္မအောင်လံ ဝိပဿနာရိပ်သာ သတင်းနှင့် ဓာတ်ပုံများ" />
            </Helmet>
            <div
                className="flex items-center justify-between gap-4"
            >
                <PageTitle>
                    တရားစခန်းပွဲ သတင်းနှင့် ဓာတ်ပုံများ
                </PageTitle>

                <Space
                    size={"large"}
                >
                    <DeleteConfirmationModal
                        onDelete={onDeleteMultiplePosts}
                        isDeleting={isDeleting}
                        title="Delete Posts"
                        description="Are you sure you want to delete the selected posts?"
                        buttonProps={{
                            type: "primary",
                            size: "large",
                            className: "!font-poppins",
                            hidden: selectedRowKeys.length === 0
                        }}
                    />
                    <CreateNewPost />
                </Space>
            </div>
            <Divider />
            <AntdTable
                columns={columns}
                dataSource={posts}
                rowKey={(record) => record.id}
                rowSelection={rowSelection}
                pagination={{
                    size: "default",
                    current: currentPage,
                    onChange: (page) => setCurrentPage(page),
                    total: totalElements,
                    showSizeChanger: false,
                    pageSize: size,
                }}
                loading={isLoading}
            />
        </AnimatedComponent>
    )
}
